import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { dirClass, dirLabel, fmtCountdown, fmtDate, fmtPct, fmtTime, formatMoney, signClass, useNow } from '../lib/format';
import type { AccountSnapshot, TradeView } from '../lib/types';
import type { HandoverView, SetupView } from '../lib/types-setups';
import { Bar, Card, CardHeader, Empty, Label, PageHeader, Pill } from '../components/ui';
import { NextUpdate, SetupStatusPill, zoneText } from './Setups';
import { periodText } from './Handovers';
import { MyTotals } from '../components/MyTotals';
import { RequiredLearning } from '../components/library/RequiredLearning';
import { OpenPnl } from '../components/PriceMark';

interface TodayResp {
  now: string;
  due: { id: string; type: string; status: string; dueAt: string; graceUntil: string; missAt: string; sequence: number | null; market: { id: string; code: string } | null; trade: { id: string; symbol: string; direction: string } | null; setup?: { id: string; symbol: string; direction: string } | null; handoverId?: string | null; forOwner?: { id: string; name: string } | null }[];
  setups: SetupView[];
  webinars?: { id: string; title: string; startsAt: string; durationMinutes: number; status: 'SCHEDULED' | 'LIVE'; host: string }[];
  handovers: { requests: HandoverView[]; covering: HandoverView[]; given: HandoverView[] };
  openTrades: (TradeView & { nextReport: { dueAt: string; status: string; sequence: number | null } | null })[];
  plans: { id: string; status: string; symbol: string; direction: string; account: string; riskPct: string | null; approvalExpiresAt: string | null; slaDueAt: string | null }[];
  accounts: (AccountSnapshot & { market: string })[];
  floor: { analysesPublished24h: number; tradesOpened24h: number };
  /** INT-ANL-04: my recent analyses whose trading day is over, with symbols still to review */
  reviews?: { analysisId: string; market: { id: string; code: string }; tradingDay: string; toReview: number; total: number }[];
}

const DUE_LABEL: Record<string, string> = { ANALYSIS: 'Daily analysis', PROGRESS_REPORT: 'Progress report', CLOSING_REPORT: 'Closing report', SETUP_UPDATE: 'Setup update', HANDOVER_ACCEPT: 'Handover request' };

type DueRow = TodayResp['due'][number];
const dueSubject = (d: DueRow) => (d.type === 'ANALYSIS' ? d.market?.code : d.type === 'SETUP_UPDATE' ? d.setup?.symbol : d.type === 'HANDOVER_ACCEPT' ? `from ${d.forOwner?.name ?? 'a trader'}` : d.trade?.symbol);
const dueLink = (d: DueRow) => (d.type === 'ANALYSIS' ? `/analysis?market=${d.market?.id}` : d.type === 'SETUP_UPDATE' && d.setup ? `/setups/${d.setup.id}` : d.type === 'HANDOVER_ACCEPT' ? '/handovers' : d.trade ? `/trades/${d.trade.id}` : '/');
const dueCta: Record<string, string> = { ANALYSIS: 'Open analysis', SETUP_UPDATE: 'Post update', HANDOVER_ACCEPT: 'Answer' };

export function Today() {
  const me = useMe();
  const now = useNow(1000);
  const tz = me.data?.timezone ?? 'UTC';
  const { data, isLoading } = useQuery({ queryKey: ['today'], queryFn: () => api<TodayResp>('/dashboards/today'), refetchInterval: 60_000 });

  const dueCards = [
    ...(data?.due ?? []).map((d) => {
      const late = d.status === 'OVERDUE' || d.status === 'MISSED' || new Date(d.dueAt).getTime() < now;
      const forWhom = d.forOwner && d.type !== 'HANDOVER_ACCEPT' ? ` · for ${d.forOwner.name}` : '';
      return {
        key: d.id,
        label: `${DUE_LABEL[d.type] ?? d.type} · ${dueSubject(d)}`,
        title:
          (d.type === 'PROGRESS_REPORT' ? `Report #${d.sequence} due ${fmtTime(d.dueAt, tz)}` : d.type === 'ANALYSIS' ? `Deadline ${fmtTime(d.dueAt, tz)}` : d.type === 'SETUP_UPDATE' ? `Update due ${fmtTime(d.dueAt, tz)}` : d.type === 'HANDOVER_ACCEPT' ? `Accept or decline by ${fmtTime(d.dueAt, tz)}` : `Due by ${fmtTime(d.dueAt, tz, true)}`) + forWhom,
        pill: d.status === 'MISSED' ? 'Missed' : late ? `Overdue ${fmtCountdown(d.dueAt, now).replace('overdue ', '')}` : `Due ${fmtCountdown(d.dueAt, now)}`,
        tone: (d.status === 'MISSED' || late ? 'down' : new Date(d.dueAt).getTime() - now < 3600_000 ? 'warn' : 'accent') as 'down' | 'warn' | 'accent',
        link: dueLink(d),
        cta: dueCta[d.type] ?? 'Report now',
        at: new Date(d.dueAt).getTime(),
      };
    }),
    ...(data?.plans ?? [])
      .filter((p) => p.status !== 'DRAFT')
      .map((p) => ({
        key: p.id,
        label: `Trade plan · ${p.symbol} ${p.direction === 'LONG' ? 'long' : 'short'}`,
        title: `${fmtPct(p.riskPct)} risk · ${p.account}`,
        pill: p.status === 'PENDING_APPROVAL' ? `With Risk · ${p.slaDueAt ? fmtCountdown(p.slaDueAt, now) : ''}` : `Approved · expires ${p.approvalExpiresAt ? fmtCountdown(p.approvalExpiresAt, now) : ''}`,
        tone: (p.status === 'PENDING_APPROVAL' ? 'accent' : 'up') as 'accent' | 'up',
        link: `/plans/${p.id}`,
        cta: p.status === 'PENDING_APPROVAL' ? 'View plan' : 'Record entry',
        at: new Date(p.slaDueAt ?? p.approvalExpiresAt ?? now).getTime(),
      })),
  ].sort((a, b) => a.at - b.at);

  return (
    <>
      <PageHeader title="Today" sub={`${fmtDate(new Date(now), tz)} · times in ${tz}`} />

      <section aria-label="Due next" className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        {isLoading && <Card className="p-4 text-sub">Loading…</Card>}
        {!isLoading && dueCards.length === 0 && <Card className="p-4 text-sub sm:col-span-2 xl:col-span-3">Nothing due right now.</Card>}
        {dueCards.map((c) => (
          <Card key={c.key} tone={c.tone} className="flex flex-col gap-2.5 p-4">
            <div className="flex items-center justify-between gap-2">
              <Label>{c.label}</Label>
              <Pill tone={c.tone}>{c.pill}</Pill>
            </div>
            <span className="text-base font-semibold">{c.title}</span>
            <Link to={c.link} className={`self-start rounded-lg px-3.5 py-2.5 font-semibold no-underline ${c.tone === 'down' ? 'bg-down text-bg' : c.tone === 'warn' ? 'bg-accent text-bg' : 'border border-[#2b3340] bg-[#1a1f27] text-ink'}`}>
              {c.cta}
            </Link>
          </Card>
        ))}
      </section>

      {!!data?.reviews?.length && (
        <Card className="flex flex-col">
          <CardHeader title="Review yesterday's analysis" right={<span className="text-xs text-muted">feeds your accuracy in Analytics</span>} />
          <div>
          {data.reviews.map((r) => (
            <div key={r.analysisId} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
              <span>
                <span className="font-semibold">{r.market.code}</span> <span className="text-sub">· {fmtDate(`${r.tradingDay}T12:00:00Z`, 'UTC')}</span>{' '}
                <span className="text-sm text-muted">
                  · {r.toReview} of {r.total} symbol{r.total === 1 ? '' : 's'} to review
                </span>
              </span>
              <Link to={`/analyses/${r.analysisId}?review=1`} className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3 py-1.5 text-sm font-semibold text-ink no-underline hover:text-ink" aria-label={`Review ${r.market.code} analysis for ${r.tradingDay}`}>
                Review
              </Link>
            </div>
          ))}
          </div>
        </Card>
      )}

      <RequiredLearning />

      <Card>
        <CardHeader title={<>Open trades <span className="font-normal text-muted">· {data?.openTrades.length ?? 0}</span></>} right={<Link to="/plans/new" className="font-semibold">+ New trade plan</Link>} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                {['Symbol', 'Account', 'Side', 'Entry', 'Stop', 'Target', 'Initial risk', 'Open (delayed price)', 'Next report'].map((h, i) => (
                  <th key={h} className={`px-4 py-2.5 font-medium ${i >= 3 && i <= 7 ? 'text-right' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.openTrades.map((t) => {
                const overdue = t.nextReport && (t.nextReport.status === 'OVERDUE' || new Date(t.nextReport.dueAt).getTime() < now);
                return (
                  <tr key={t.id} className="border-t border-[#1a1f27]">
                    <td className="px-4 py-3 font-semibold">
                      <Link to={`/trades/${t.id}`}>{t.symbol}</Link>
                    </td>
                    <td className="px-4 py-3 text-sub">{t.account.code}</td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${dirClass(t.direction)}`}>{dirLabel(t.direction)}</span> <span className="text-muted">{t.sizeOpen}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{t.avgEntry}</td>
                    <td className="px-4 py-3 text-right font-mono text-sub">{t.stopLoss ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-mono text-sub">{t.takeProfits[0]?.price ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.initialRiskAmount && t.currency ? formatMoney(t.initialRiskAmount, t.currency) : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <OpenPnl mark={t.mark} tz={tz} />
                    </td>
                    <td className="px-4 py-3">
                      {t.nextReport ? (
                        overdue ? (
                          <Link to={`/trades/${t.id}`}>
                            <Pill tone="down">#{t.nextReport.sequence} {fmtCountdown(t.nextReport.dueAt, now)}</Pill>
                          </Link>
                        ) : (
                          <span className="font-mono">
                            #{t.nextReport.sequence} {fmtTime(t.nextReport.dueAt, tz)} <span className="text-xs text-muted">({fmtCountdown(t.nextReport.dueAt, now)})</span>
                          </span>
                        )
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!isLoading && !data?.openTrades.length && <Empty>No open trades.</Empty>}
        </div>
      </Card>

      <Card>
        <CardHeader
          title={<>My setups <span className="font-normal text-muted">· {data?.setups.length ?? 0}</span></>}
          right={
            <span className="flex gap-4">
              <Link to="/setups/new" className="font-semibold">+ Mark a setup</Link>
              <Link to="/setups?scope=mine">All setups</Link>
            </span>
          }
        />
        {data?.setups.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
            <div className="flex min-w-[220px] flex-col gap-0.5">
              <Link to={`/setups/${s.id}`} className="font-semibold text-ink">
                {s.symbol} <span className={dirClass(s.direction)}>{dirLabel(s.direction)}</span> <span className="font-normal text-muted">· {s.timeframe} · zone {zoneText(s)}</span>
              </Link>
              <span className="text-xs text-muted">
                {s.owner.id === me.data?.id ? s.cadenceLabel : `Covering for ${s.owner.name}`} · expires {fmtTime(s.expiresAt, tz, true)}
              </span>
            </div>
            <SetupStatusPill status={s.status} stale={s.stale} />
            <span className="min-w-[200px] text-sm">
              <NextUpdate s={s} now={now} tz={tz} short />
            </span>
            <Link to={`/setups/${s.id}`} className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3 py-1.5 text-sm font-semibold text-ink no-underline hover:text-ink">
              Post update
            </Link>
          </div>
        ))}
        {!isLoading && !data?.setups.length && <Empty>No active setups. Mark one when you start watching a level.</Empty>}
      </Card>

      {!!data?.webinars?.length && (
        <Card tone={data.webinars.some((w) => w.status === 'LIVE') ? 'up' : 'accent'} className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[15px] font-semibold">Webinars today</h2>
            <Link to="/webinars" className="text-sm">All webinars →</Link>
          </div>
          {data.webinars.map((w) => (
            <Link key={w.id} to={w.status === 'LIVE' ? `/webinars/${w.id}/live` : `/webinars/${w.id}`} className="flex flex-wrap items-center justify-between gap-2 text-ink no-underline hover:text-ink">
              <span>
                {w.status === 'LIVE' ? <Pill tone="up">Live now</Pill> : <Pill tone="accent">{fmtTime(w.startsAt, tz)}</Pill>} <span className="font-semibold">{w.title}</span>
              </span>
              <span className="text-sm text-muted">{w.host} · {w.durationMinutes} min</span>
            </Link>
          ))}
        </Card>
      )}

      {!!data && (data.handovers.covering.length > 0 || data.handovers.given.length > 0) && (
        <Card tone="live" className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[15px] font-semibold">Handovers</h2>
            <Link to="/handovers" className="text-sm">Manage →</Link>
          </div>
          {data.handovers.covering.map((h) => (
            <span key={h.id} className="text-ink2">
              <Pill tone="live">{h.coveringNow ? 'Covering now' : 'You cover'}</Pill> {h.owner.name}'s {h.label} · {periodText(h, tz)}
            </span>
          ))}
          {data.handovers.given.map((h) => (
            <span key={h.id} className="text-ink2">
              <Pill tone={h.status === 'REQUESTED' ? 'warn' : 'live'}>{h.status === 'REQUESTED' ? 'Waiting for answer' : h.coveringNow ? 'Covered now' : 'Cover arranged'}</Pill> {h.cover.name} · your {h.label} · {periodText(h, tz)}
            </span>
          ))}
        </Card>
      )}

      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">My accounts</h2>
          <MyTotals />
          {data?.accounts.map((a) => {
            const limit = Number(a.policy.dailyLossLimitPct);
            const maxOpen = Number(a.policy.maxOpenRiskPct);
            const openPct = Number(a.equity) > 0 ? (Number(a.openRiskAmount) / Number(a.equity)) * 100 : 0;
            return (
              <div key={a.id} className="flex flex-col gap-2.5 rounded-lg bg-raised p-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold">
                    {a.code} <span className="font-normal text-muted">· {a.market} · {a.currency}</span>
                  </span>
                  <span className="font-mono text-[15px] font-semibold">{formatMoney(a.equity, a.currency)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Realised today</span>
                  <span className={`font-semibold ${signClass(a.realisedPnlToday)}`}>{formatMoney(a.realisedPnlToday, a.currency, { signed: true })}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between text-xs text-sub">
                    <span>Open risk</span>
                    <span>
                      {fmtPct(openPct, 1)} of {fmtPct(maxOpen, 1)}
                    </span>
                  </div>
                  <Bar pct={(openPct / maxOpen) * 100} />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between text-xs text-sub">
                    <span>Daily loss used{a.dailyLossLocked ? ' · LOCKED' : ''}</span>
                    <span>
                      {fmtPct(a.dailyLossUsedPct)} of {fmtPct(limit, 1)}
                    </span>
                  </div>
                  <Bar pct={(Number(a.dailyLossUsedPct) / limit) * 100} color={a.dailyLossLocked ? '#ff7a6e' : '#f5b544'} />
                </div>
              </div>
            );
          })}
        </Card>
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">On the floor (24 h)</h2>
          <Link to="/floor" className="flex flex-col gap-1 rounded-lg bg-raised p-3 text-ink no-underline">
            <span className="text-base font-semibold">{data?.floor.analysesPublished24h ?? 0} analyses published</span>
            <span className="text-sub">{data?.floor.tradesOpened24h ?? 0} trades opened · open the floor feed →</span>
          </Link>
          <Link to="/analysis" className="rounded-lg border border-line2 p-3 font-semibold no-underline">
            Write today's analysis →
          </Link>
          <span className="text-xs text-muted">Open R appears in each trade's progress reports, and next to it on Today when a delayed price is available (labelled with its source and time — not live).</span>
          <div className="flex flex-col gap-2 border-t border-line pt-3">
            <div className="flex items-baseline justify-between">
              <h3 className="m-0 text-sm font-semibold">My symbols</h3>
              <Link to="/symbols" className="text-sm font-semibold">Manage →</Link>
            </div>
            {me.data && !me.data.symbols.length && <span className="text-sm text-sub">No symbols assigned yet. Ask for one from My symbols.</span>}
            <div className="flex flex-wrap gap-1.5">
              {me.data?.symbols.map((s) => (
                <span key={s.id} title={s.displayName} className="rounded-full border border-line2 bg-raised px-2.5 py-1 font-mono text-xs">
                  {s.symbol} <span className="text-muted">· {s.marketCode}</span>
                </span>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
